import React, { useCallback, useContext, useState } from "react";
import { Avatar, Box, Button, Chip, CircularProgress, IconButton, Stack, TextField, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link as RouterLink } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import EmptyPage from "components/common/EmptyPage";
import CsvDownloadButton, { csvFilename } from "components/common/CsvDownloadButton";
import performRequest from "network/perform-request";
import {
  leagueCustomerRosterMemberRoute,
  leagueCustomerRosterMembersCsvRoute,
  leagueCustomerRosterMembersRoute,
} from "network/api-routes";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import { useGestionPaginatedList } from "hooks/useGestionPaginatedList";
import GestionListPagination from "components/pages/Gestion/GestionListPagination";
import GestionPlayerUserLinkDialog from "components/pages/Gestion/GestionPlayerUserLinkDialog";
import PlayerFormDialog from "components/pages/Gestion/PlayerFormDialog";
import { playerInitials } from "components/pages/Gestion/player-form-helpers";
import GestionTable from "components/pages/Gestion/GestionTable";
import GestionPageHeader from "components/pages/Gestion/GestionPageHeader";
import FinanceOnly from "components/pages/Gestion/FinanceOnly";
const GestionPlayers = () => {
  const { customerId, customerName } = useGestionCustomer();
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [linkUserPlayer, setLinkUserPlayer] = useState(null);

  const handleFetchError = useCallback(
    (error) => {
      enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
        variant: "error",
      });
    },
    [enqueueSnackbar, intl],
  );

  const {
    items: players,
    search,
    page,
    pageSize,
    totalPages,
    totalResults,
    initialLoading,
    refreshing,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    reload,
  } = useGestionPaginatedList({
    customerId,
    userId: user?.id,
    user,
    route: leagueCustomerRosterMembersRoute,
    onError: handleFetchError,
  });

  const openCreateDialog = () => {
    setEditingPlayer(null);
    setDialogOpen(true);
  };

  const openEditDialog = (player) => {
    setEditingPlayer(player);
    setDialogOpen(true);
  };

  const toggleArchive = (player) => {
    if (!customerId) return;
    performRequest(
      "PATCH",
      leagueCustomerRosterMemberRoute(customerId, player.id),
      { archived: !player.archived },
      true,
      user,
    )
      .then(() => reload())
      .catch(handleFetchError);
  };

  if (!customerId) return null;

  return (
    <Box sx={{ width: "100%", maxWidth: "lg", mx: "auto", p: { xs: 3, md: 4 } }}>
      <Stack spacing={3}>
        <GestionPageHeader
          title={intl.formatMessage({ id: "gestion.players.title" }, { name: customerName })}
          subtitle={intl.formatMessage({ id: "gestion.players.subtitle" })}
          actions={
            <>
              <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon />} onClick={openCreateDialog}>
                {intl.formatMessage({ id: "gestion.players.create" })}
              </Button>
              <CsvDownloadButton
                labelId="league.csv.exportPlayers"
                url={leagueCustomerRosterMembersCsvRoute(customerId)}
                filename={csvFilename(customerName, "players")}
                user={user}
              />
            </>
          }
        />

        <TextField
          size="small"
          fullWidth
          label={intl.formatMessage({ id: "gestion.players.search" })}
          value={search}
          onChange={handleSearchChange}
        />

      <GestionListPagination
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        totalResults={totalResults}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        disabled={initialLoading || refreshing}
      />

      {initialLoading ? (
        <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Box>
      ) : players.length === 0 ? (
        <EmptyPage text={intl.formatMessage({ id: "gestion.players.empty" })} />
      ) : (
        <Box sx={{ position: "relative", opacity: refreshing ? 0.6 : 1, transition: "opacity 0.2s" }}>
          {refreshing && (
            <CircularProgress size={24} sx={{ position: "absolute", top: 16, right: 16, zIndex: 1 }} />
          )}
          <GestionTable
            getRowSx={(player) => ({ opacity: player.archived ? 0.6 : 1 })}
            headers={[
              {
                key: "avatar",
                title: "",
                width: 56,
                renderCell: (_value, player) => (
                  <Avatar src={player.profile_pic_url || undefined} sx={{ width: 36, height: 36, bgcolor: "action.hover" }}>
                    {playerInitials(player)}
                  </Avatar>
                ),
              },
              {
                key: "name",
                title: intl.formatMessage({ id: "gestion.players.name" }),
                renderCell: (_value, player) => {
                  const isLinked = Boolean(player.user_id || player.is_associated_to_user || player.linked_user);
                  return (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>{[player.first_name, player.last_name].filter(Boolean).join(" ")}</span>
                      {isLinked && <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />}
                    </Stack>
                  );
                },
              },
              { key: "document_number", title: intl.formatMessage({ id: "gestion.players.document" }) },
              { key: "member_number", title: intl.formatMessage({ id: "gestion.players.memberNumber" }) },
              { key: "email", title: intl.formatMessage({ id: "gestion.players.email" }) },
              {
                key: "revaUser",
                title: intl.formatMessage({ id: "gestion.players.revaUser" }),
                renderCell: (_value, player) => {
                  const isLinked = Boolean(player.user_id || player.is_associated_to_user || player.linked_user);
                  if (isLinked) {
                    return (
                      <Tooltip title={player.linked_user?.full_name || intl.formatMessage({ id: "gestion.players.associatedToUser" })}>
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          icon={<CheckCircleIcon />}
                          label={player.linked_user?.full_name || intl.formatMessage({ id: "gestion.players.linkedShort" })}
                          onClick={() => setLinkUserPlayer(player)}
                          sx={{ maxWidth: 180 }}
                        />
                      </Tooltip>
                    );
                  }
                  return (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={intl.formatMessage({ id: "gestion.players.notLinkedShort" })}
                      onClick={() => setLinkUserPlayer(player)}
                    />
                  );
                },
              },
              {
                key: "actions",
                title: intl.formatMessage({ id: "gestion.actions" }),
                align: "right",
                renderCell: (_value, player) => {
                  const isLinked = Boolean(player.user_id || player.is_associated_to_user || player.linked_user);
                  return (
                    <>
                      <Tooltip
                        title={intl.formatMessage({
                          id: isLinked ? "gestion.players.manageUserLink" : "gestion.players.linkUser",
                        })}
                      >
                        <IconButton onClick={() => setLinkUserPlayer(player)} size="small">
                          {isLinked ? <LinkOffIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <FinanceOnly>
                        <Tooltip title={intl.formatMessage({ id: "gestion.players.financials" })}>
                          <IconButton
                            component={RouterLink}
                            to={GESTION_ROUTES.playerFinancials(customerId, player.id)}
                            size="small"
                          >
                            <AttachMoneyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </FinanceOnly>
                      <Tooltip title={intl.formatMessage({ id: "gestion.edit" })}>
                        <IconButton onClick={() => openEditDialog(player)} size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={intl.formatMessage({
                          id: player.archived ? "gestion.unarchive" : "gestion.archive",
                        })}
                      >
                        <IconButton onClick={() => toggleArchive(player)} size="small">
                          {player.archived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </>
                  );
                },
              },
            ]}
            rows={players}
          />
        </Box>
      )}

      <GestionListPagination
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        totalResults={totalResults}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        disabled={initialLoading || refreshing}
      />
      </Stack>

      <PlayerFormDialog
        open={dialogOpen}
        customerId={customerId}
        player={editingPlayer}
        familyCandidates={players}
        onClose={() => setDialogOpen(false)}
        onChanged={reload}
      />

      <GestionPlayerUserLinkDialog
        open={Boolean(linkUserPlayer)}
        onClose={() => setLinkUserPlayer(null)}
        customerId={customerId}
        player={linkUserPlayer}
        onUpdated={reload}
      />
    </Box>
  );
};

export default GestionPlayers;
